import { useContainerWidth } from "./flowsChartShared";
import "../pairedBarChart.css";

/**
 * The shared paired-bar component (2026-10-27, first built for the
 * macro-factors article's "co-movement vs forecast" contrast) -- for each
 * category, two horizontal bars on a shared scale. Hand-rolled SVG, same
 * precedent as ValuationBandChart.tsx/DriftCurveChart.tsx. Deliberately
 * generic (labelA/labelB, colorA/colorB, a formatter) so a future article
 * needing "two numbers per category, side by side" reuses this rather
 * than building a third bar-chart component.
 */

export interface PairedBarRow {
  label: string;
  valueA: number | null;
  valueB: number | null;
  /** Shown instead of a bar when valueA/valueB is null (e.g. "no natural
   * pairing" for a level-based factor) -- never silently blank. */
  naReasonA?: string;
  naReasonB?: string;
}

const ROW_HEIGHT = 46;
const MARGIN = { top: 8, right: 54, bottom: 8, left: 170 };

export function PairedBarChart({
  rows, labelA, labelB, colorA, colorB, formatValue = (v) => v.toFixed(3),
}: {
  rows: PairedBarRow[];
  labelA: string;
  labelB: string;
  colorA: string;
  colorB: string;
  formatValue?: (v: number) => string;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  if (rows.length === 0) {
    return <div ref={ref} className="empty-state" style={{ minHeight: 80 }}>No data for this selection.</div>;
  }
  if (width === 0) {
    return <div ref={ref} style={{ minHeight: rows.length * ROW_HEIGHT * 2 + 20 }} />;
  }

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const maxAbs = Math.max(
    0.001,
    ...rows.flatMap((r) => [r.valueA !== null ? Math.abs(r.valueA) : 0, r.valueB !== null ? Math.abs(r.valueB) : 0]),
  );
  const barW = (v: number) => (Math.abs(v) / maxAbs) * plotW;
  const totalHeight = rows.length * ROW_HEIGHT * 2 + 24;

  return (
    <div ref={ref}>
      <div className="pbc-legend">
        <span><i style={{ background: colorA }} /> {labelA}</span>
        <span><i style={{ background: colorB }} /> {labelB}</span>
      </div>
      <svg width={width} height={totalHeight} role="img" aria-label="Paired bar comparison">
        {rows.map((row, i) => {
          const yBase = 12 + i * ROW_HEIGHT * 2;
          return (
            <g key={row.label}>
              <text x={MARGIN.left - 8} y={yBase + 24} fontSize={11} fill="#3a3f4b" textAnchor="end">
                {row.label}
              </text>
              {/* Bar A (contemporaneous) */}
              {row.valueA !== null ? (
                <>
                  <rect x={MARGIN.left} y={yBase} width={barW(row.valueA)} height={16} fill={colorA} rx={2} />
                  <text x={MARGIN.left + barW(row.valueA) + 6} y={yBase + 12} fontSize={10} fill="#3a3f4b">
                    {formatValue(row.valueA)}
                  </text>
                </>
              ) : (
                <text x={MARGIN.left} y={yBase + 12} fontSize={10} fill="#8a91a0" fontStyle="italic">
                  {row.naReasonA ?? "n/a"}
                </text>
              )}
              {/* Bar B (predictive) */}
              {row.valueB !== null ? (
                <>
                  <rect x={MARGIN.left} y={yBase + 20} width={barW(row.valueB)} height={16} fill={colorB} rx={2} />
                  <text x={MARGIN.left + barW(row.valueB) + 6} y={yBase + 32} fontSize={10} fill="#3a3f4b">
                    {formatValue(row.valueB)}
                  </text>
                </>
              ) : (
                <text x={MARGIN.left} y={yBase + 32} fontSize={10} fill="#8a91a0" fontStyle="italic">
                  {row.naReasonB ?? "n/a"}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
